/**
 * Subscription checkout page: URL params -> Storefront API cartCreate -> checkoutUrl.
 * URL: ?email=..&listVariantIds1=[1,2]&sellingPlanId1=10&quantity1=2&listVariantIds2=..
 * &mode=2 (optional): a group without sellingPlanIdN becomes a one-time purchase.
 */
var SubscriptionCheckoutPage = (function () {
  var API_VERSION = '2026-07';

  // Throws Error(message) on invalid input. Returns { email, lines }.
  function parseParams(search) {
    var q = new URLSearchParams(search);
    var email = q.get('email') || '';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error('email が正しくありません');

    var mode = q.has('mode') ? q.get('mode') : '1';
    if (mode !== '1' && mode !== '2') throw new Error('mode が正しくありません');

    var lines = [];
    for (var n = 1; q.has('listVariantIds' + n); n++) {
      var plan = q.get('sellingPlanId' + n) || '';
      var oneTime = mode === '2' && !q.has('sellingPlanId' + n);
      var qty = q.has('quantity' + n) ? q.get('quantity' + n) : '1';
      if (!oneTime && !/^\d+$/.test(plan)) throw new Error('sellingPlanId' + n + ' が正しくありません');
      if (!/^[1-9]\d*$/.test(qty)) throw new Error('quantity' + n + ' が正しくありません');
      var ids = q.get('listVariantIds' + n).replace(/[\[\]\s]/g, '').split(',');
      for (var i = 0; i < ids.length; i++) {
        if (!/^\d+$/.test(ids[i])) throw new Error('listVariantIds' + n + ' が正しくありません');
        var line = { merchandiseId: 'gid://shopify/ProductVariant/' + ids[i], quantity: Number(qty) };
        if (!oneTime) line.sellingPlanId = 'gid://shopify/SellingPlan/' + plan;
        lines.push(line);
      }
    }
    if (!lines.length) throw new Error('listVariantIds1 がありません');
    return { email: email, lines: lines };
  }

  async function request(query, variables, cfg) {
    var res = await fetch('https://' + cfg.shop + '/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token,
      },
      body: JSON.stringify({ query: query, variables: variables }),
      signal: AbortSignal.timeout(15000),
    });
    if (!res.ok) throw new Error('Storefront request failed');
    var json = await res.json();
    if (json.errors && json.errors.length) throw new Error(json.errors.map(function (e) { return e.message; }).join(' / '));
    return json.data;
  }

  async function loadBundleVariant(id, cfg) {
    var data = await request('query BundleVariant($id: ID!) { node(id: $id) { ... on ProductVariant {' +
      'id availableForSale requiresComponents product { id } components(first: 20) {' +
      'pageInfo { hasNextPage } nodes { quantity productVariant { id title availableForSale requiresComponents ' +
      'product { title requiresSellingPlan } sellingPlanAllocations(first: 20) {' +
      'pageInfo { hasNextPage } nodes { sellingPlan { id name } } } } } } } } }', { id: id }, cfg);
    if (!data || !data.node) throw new Error('Product variant is unavailable');
    return data.node;
  }

  function buildBundleLines(variant, quantity, selections, groupId) {
    var graph = variant.components;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || !groupId || !variant.availableForSale ||
      !graph || graph.pageInfo.hasNextPage || !graph.nodes.length || graph.nodes.length > 20)
      throw new Error('Bundle is unavailable');
    return graph.nodes.map(function (component) {
      var child = component.productVariant;
      var allocations = child.sellingPlanAllocations;
      var qty = quantity * component.quantity;
      if (!child.availableForSale || child.requiresComponents || !Number.isSafeInteger(component.quantity) || component.quantity < 1 ||
        !Number.isSafeInteger(qty) || !allocations || allocations.pageInfo.hasNextPage)
        throw new Error('Bundle component is unavailable');
      var plan = selections[child.id];
      if ((allocations.nodes.length && plan === undefined) || (child.product.requiresSellingPlan && !plan) ||
        (plan && !allocations.nodes.some(function (a) { return a.sellingPlan.id === plan; })))
        throw new Error('Select a valid selling plan for ' + child.product.title);
      var line = { merchandiseId: child.id, quantity: qty, attributes: [
        { key: '_os_bundle_group_id', value: groupId },
        { key: '_os_bundle_parent_product_id', value: variant.product.id.split('/').pop() },
        { key: '_os_bundle_parent_variant_id', value: variant.id.split('/').pop() },
      ] };
      if (plan) line.sellingPlanId = plan;
      return line;
    });
  }

  function lineTotals(lines) {
    var totals = {};
    lines.forEach(function (line) {
      var attrs = (line.attributes || []).map(function (a) { return [a.key, a.value]; }).sort();
      var key = JSON.stringify([line.merchandiseId, line.sellingPlanId || '', attrs]);
      totals[key] = (totals[key] || 0) + line.quantity;
    });
    return JSON.stringify(Object.keys(totals).sort().map(function (key) { return [key, totals[key]]; }));
  }

  async function createCheckoutUrl(parsed, cfg) {
    var input = { lines: parsed.lines };
    if (parsed.email) {
      input.buyerIdentity = { email: parsed.email };
      input.attributes = [{ key: 'input_email', value: parsed.email }];
    }
    var data = await request('mutation cartCreate($input: CartInput!) { cartCreate(input: $input) {' +
      'cart { checkoutUrl lines(first: 250) { pageInfo { hasNextPage } nodes { quantity attributes { key value } ' +
      'merchandise { ... on ProductVariant { id } } sellingPlanAllocation { sellingPlan { id } } } } } ' +
      'userErrors { message } warnings { message } } }', { input: input }, cfg);
    var result = data && data.cartCreate;
    var errors = ((result && result.userErrors) || []).concat((result && result.warnings) || []);
    if (errors.length) throw new Error(errors.map(function (e) { return e.message; }).join(' / '));
    var url = result && result.cart && result.cart.checkoutUrl;
    if (!url || !/^https:\/\//.test(url)) throw new Error('checkoutUrl を取得できませんでした');
    var graph = result.cart.lines;
    if (!graph || graph.pageInfo.hasNextPage || lineTotals(parsed.lines) !== lineTotals(graph.nodes.map(function (line) {
      return { merchandiseId: line.merchandise.id, quantity: line.quantity, attributes: line.attributes,
        sellingPlanId: line.sellingPlanAllocation && line.sellingPlanAllocation.sellingPlan.id };
    }))) throw new Error('Cart does not contain the complete requested items');
    return url;
  }

  // Reads the URL, shows status in #subscription-checkout-status, redirects on success.
  function run(cfg) {
    var el = document.getElementById('subscription-checkout-status');
    var parsed;
    try {
      parsed = parseParams(window.location.search);
    } catch (e) {
      el.textContent = e.message;
      return;
    }
    createCheckoutUrl(parsed, cfg).then(
      function (url) {
        window.location.replace(url);
      },
      function (e) {
        el.textContent = e.message;
      }
    );
  }

  return { parseParams: parseParams, createCheckoutUrl: createCheckoutUrl, loadBundleVariant: loadBundleVariant, buildBundleLines: buildBundleLines, run: run };
})();

if (typeof module !== 'undefined') module.exports = SubscriptionCheckoutPage;
